/**
 * Owns the URL ↔ focused-note coupling and the orchestration that fires
 * when a focused note is resolved (click or URL deep-link).
 *
 * - Writes the URL hash whenever `focusedNoteId` changes.
 * - Runs the focused-note orchestration (image refs, notes-filter date
 *   range, sidebar tab, map zoom) once per resolved id, with a dedup ref
 *   so incidental notes-array updates don't re-trigger, and again for each
 *   note link followed.
 *
 * URL → state on mount happens upstream: `MonitorProjectView/index.tsx`
 * parses `#note-<id>` and threads it through `NotesProvider`'s
 * `initialFocusedNoteId`. This hook handles everything that follows from
 * that initial state plus user-driven focus changes, including a `#note-<id>`
 * link followed while the page is already mounted.
 */

import * as B from '@blueprintjs/core';
import * as geojson from 'geojson';
import React from 'react';
import {
  Location,
  NavigationType,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';

import {SidebarViewState} from 'app/components/TabbedSidebar/TabbedSidebar';
import {ToastNotification} from 'app/modules/Notification/types';
import {NotesActions, NotesState, StateApiNote} from 'app/stores/NotesStore';
import * as historyUtils from 'app/utils/historyUtils';
import * as routeUtils from 'app/utils/routeUtils';

import {MapStateDispatch} from '../MapStateProvider';

const NOTES_SIDEBAR_TITLE = 'Notes';
const LOOKOUT_SIDEBAR_TITLE = 'Lookout';
const AGENT_SIDEBAR_TITLE = 'Agent';
const CHAT_SIDEBAR_TITLE = 'Chat';

interface Args {
  notesState: NotesState;
  notesActions: Pick<NotesActions, 'focusNote' | 'loadNotes'>;
  pushNotification: (p: ToastNotification) => unknown;
  mapStateDispatch: MapStateDispatch;
  /** Stable callback from useNotesFilterState. */
  extendDateRangeToInclude: (createdAt: string) => void;
  setTabbedSidebarState: (
    state: SidebarViewState | ((prev: SidebarViewState) => SidebarViewState)
  ) => void;
  zoomToGeometry: (geometry: geojson.GeoJSON | null) => void;
  focusOnFeatureGeometry: () => void;
}

/** The notes refetch made for one focused note, on one property's notes store. */
interface NoteRefetch {
  noteId: number | string;
  notesActions: Args['notesActions'];
  status: 'pending' | 'completed' | 'failed' | 'reported';
}

function expectedSidebarTitleFor(noteType: StateApiNote['noteType']): string {
  if (noteType === 'alert') return LOOKOUT_SIDEBAR_TITLE;
  if (noteType === 'agent') return AGENT_SIDEBAR_TITLE;
  return NOTES_SIDEBAR_TITLE;
}

function noteIdInHash(location: Location): number | null {
  const match = historyUtils.matchInHash(location, routeUtils.NOTE_URL_PARAM_REGEX);
  const noteId = match ? parseInt(match[2], 10) : NaN;
  return Number.isNaN(noteId) ? null : noteId;
}

function hasOpenNoteState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as Record<string, unknown>)[routeUtils.OPEN_NOTE_STATE_KEY] === true
  );
}

export default function useNoteUrlState({
  notesState,
  notesActions,
  pushNotification,
  mapStateDispatch,
  extendDateRangeToInclude,
  setTabbedSidebarState,
  zoomToGeometry,
  focusOnFeatureGeometry,
}: Args): void {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const hash = location.hash;

  // A hash that changed under a mounted page came from a navigation or from
  // this hook's own write. NotesProvider only reads the hash on mount.
  const lastHashRef = React.useRef(hash);
  // The note a URL asked for: the one the page loaded on, or one a navigation
  // named since. It opens in its own tab, where a note focused from the map or
  // from the chat's cards leaves the Chat tab showing. A ref, so the
  // orchestration below sees it in the same pass as the focus it comes with:
  // a new property's store is already focused on the note when it mounts.
  const linkedNoteRef = React.useRef<number | null>(noteIdInHash(location));

  // State → URL whenever focusedNoteId changes, and URL → state when a
  // navigation changes the hash.
  React.useEffect(() => {
    const previousHash = lastHashRef.current;
    lastHashRef.current = hash;
    if (previousHash !== hash) {
      const hashNoteId = noteIdInHash(location);
      // Back, Forward or a link the user followed: what the hash names is the
      // note to open, rather than state for the URL to overwrite. This hook's
      // own writes, and the scene's, are replaces.
      if (navigationType !== NavigationType.Replace) {
        if (hashNoteId !== null) {
          linkedNoteRef.current = hashNoteId;
          notesActions.focusNote(hashNoteId);
          return;
        }
        if (noteIdInHash({...location, hash: previousHash}) !== null) {
          // Leaving a note's URL closes the note instead of writing it back.
          linkedNoteRef.current = null;
          notesActions.focusNote(null);
          return;
        }
      } else if (hashNoteId !== null && hashNoteId !== notesState.focusedNoteId) {
        notesActions.focusNote(hashNoteId);
        return;
      }
    }

    if (notesState.focusedNoteId) {
      historyUtils.updateHash(
        navigate,
        location,
        routeUtils.NOTE_URL_PARAM_REGEX,
        `${routeUtils.NOTE_URL_PARAM}${notesState.focusedNoteId}`
      );
    } else {
      historyUtils.updateHash(navigate, location, routeUtils.NOTE_URL_PARAM_REGEX, '');
    }
    // location is read by updateHash but should not cause re-fires; hash is
    // listed so we react to external nav that affects our segment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, hash, notesState.focusedNoteId, notesActions]);

  // Orchestrate side-effects once per resolved focused-note id. The dedup
  // ref keeps incidental notes-array updates (note created elsewhere,
  // filter applied, etc.) from re-triggering zoom/scroll for the
  // currently-focused note.
  const orchestratedRef = React.useRef<number | string | null>(null);
  // The notes list is loaded once per property, so a note made since — by the
  // agent, for one — is missing from it until it's fetched again. A note is
  // only reported missing once the refetch made for it has settled.
  const [refetch, setRefetch] = React.useState<NoteRefetch | null>(null);
  // A refetch, and its report, last while focus stays on its note: following
  // the link again later fetches again, and retries one that failed.
  React.useEffect(() => {
    setRefetch((current) => (current?.noteId === notesState.focusedNoteId ? current : null));
    if (linkedNoteRef.current !== notesState.focusedNoteId) linkedNoteRef.current = null;
  }, [notesState.focusedNoteId]);

  // A note link from the chat (see LensLink) opens its note even when the hash
  // already names it. Only the push that follows it does: Back, and a replace
  // that keeps its state (useSceneUrlState), leave the tab alone.
  const noteLinkFollowed =
    navigationType === NavigationType.Push && hasOpenNoteState(location.state);
  // Counts those follows, so one to the note already focused still runs the
  // orchestration.
  const [linkFollows, setLinkFollows] = React.useState(0);
  React.useEffect(() => {
    const noteId = noteLinkFollowed ? noteIdInHash(location) : null;
    if (noteId === null) return;
    linkedNoteRef.current = noteId;
    notesActions.focusNote(noteId);
    // A new visit fetches again for a load that failed or came back without
    // the note; one still out answers for this visit too.
    setRefetch((current) =>
      current?.noteId === noteId && current.status !== 'pending' ? null : current
    );
    setLinkFollows((count) => count + 1);
    // Keyed on the entry: each link followed opens its note once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteLinkFollowed, location.key]);

  React.useEffect(() => {
    if (!notesState.focusedNoteId) {
      orchestratedRef.current = null;
      return;
    }

    const note = notesState.notes.find((n) => n.id === notesState.focusedNoteId);
    if (!note) {
      const ownRefetch =
        refetch?.noteId === notesState.focusedNoteId && refetch.notesActions === notesActions
          ? refetch
          : null;
      if (!ownRefetch) {
        // An empty list is still on its first load, unless that load failed.
        if (notesState.notes.length === 0 && !notesState.loadingError) return;
        const started: NoteRefetch = {
          noteId: notesState.focusedNoteId,
          notesActions,
          status: 'pending',
        };
        const settle = (status: NoteRefetch['status']) =>
          setRefetch((current) => (current === started ? {...started, status} : current));
        setRefetch(started);
        notesActions.loadNotes().then(
          () => settle('completed'),
          () => settle('failed')
        );
        return;
      }
      if (ownRefetch.status === 'completed' || ownRefetch.status === 'failed') {
        setRefetch({...ownRefetch, status: 'reported'});
        pushNotification({
          message: 'There was a problem loading the note.',
          options: {intent: B.Intent.DANGER},
        });
      }
      return;
    }

    extendDateRangeToInclude(note.createdAt);

    // A followed link opens its note each time, even the one already focused.
    const linked = linkedNoteRef.current === notesState.focusedNoteId;
    if (!linked && orchestratedRef.current === notesState.focusedNoteId) return;
    orchestratedRef.current = notesState.focusedNoteId;
    if (linked) linkedNoteRef.current = null;

    mapStateDispatch({type: 'SET_IMAGE_REFS', imageRefs: note.imageRefs});
    // A note focused from the chat — a card the agent showed — leaves the
    // conversation on screen. A note a link named opens in its own tab.
    setTabbedSidebarState((prev) =>
      !linked && prev.isExpanded && prev.currentViewTitle === CHAT_SIDEBAR_TITLE
        ? prev
        : {isExpanded: true, currentViewTitle: expectedSidebarTitleFor(note.noteType)}
    );
    if (note.geometry) {
      zoomToGeometry(note.geometry);
    } else {
      focusOnFeatureGeometry();
    }
  }, [
    notesState.focusedNoteId,
    notesState.notes,
    notesState.loadingError,
    notesActions,
    refetch,
    linkFollows,
    pushNotification,
    mapStateDispatch,
    extendDateRangeToInclude,
    setTabbedSidebarState,
    zoomToGeometry,
    focusOnFeatureGeometry,
  ]);
}
